import { useEffect, useRef, useCallback, useState } from 'react';

import { useOnlyOfficeConfig } from '@/api/usePresentationWizard';
import type { PresentationItem } from '@/types/presentation';
import toast from 'react-hot-toast';
import styles from './PresentationEditor.module.css';

declare global {
  interface Window {
    DocsAPI?: {
      DocEditor: new (elementId: string, config: Record<string, unknown>) => DocsEditorInstance;
    };
  }
}

interface DocsEditorInstance {
  destroyEditor: () => void;
}

interface Props {
  presentation: PresentationItem;
  mode?: 'edit' | 'view';
}

const loadScript = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ONLYOFFICE API script`));
    document.head.appendChild(script);
  });
};

const collectDeckImageUrls = (presentation: PresentationItem): string[] => {
  const urls = new Set<string>();
  const deck = presentation.deck_json;
  if (deck?.slides) {
    for (const slide of deck.slides) {
      if (slide.image_url) urls.add(slide.image_url);
      if (slide.content_images) {
        for (const ci of slide.content_images) {
          if (ci.url) urls.add(ci.url);
        }
      }
    }
  }
  for (const u of presentation.generated_images || []) urls.add(u);
  for (const u of presentation.reference_images || []) urls.add(u);
  for (const u of presentation.venue_images || []) urls.add(u);
  if (presentation.logo_url) urls.add(presentation.logo_url);
  return Array.from(urls);
};

const prewarmImages = (urls: string[]): Promise<void> => {
  if (!urls.length) return Promise.resolve();
  return new Promise((resolve) => {
    let remaining = urls.length;
    const done = () => {
      remaining -= 1;
      if (remaining <= 0) resolve();
    };
    const timer = window.setTimeout(() => resolve(), 4000);
    for (const url of urls) {
      const img = new Image();
      img.onload = () => {
        done();
        if (remaining <= 0) window.clearTimeout(timer);
      };
      img.onerror = () => {
        done();
        if (remaining <= 0) window.clearTimeout(timer);
      };
      img.src = url;
    }
  });
};

// Stable IDs so ONLYOFFICE's DocEditor has something to attach to.  We
// ping-pong between slots A and B on every config change: the new editor
// mounts in the idle slot while the previous one stays visible, then we
// cross-fade and destroy the old instance.  Result: no skeleton flash on
// chat-triggered reloads.
const SLOT_A_ID = 'onlyoffice-editor-a';
const SLOT_B_ID = 'onlyoffice-editor-b';

export const PresentationEditor = ({ presentation, mode = 'edit' }: Props) => {
  const containerARef = useRef<HTMLDivElement>(null);
  const containerBRef = useRef<HTMLDivElement>(null);
  const editorARef = useRef<DocsEditorInstance | null>(null);
  const editorBRef = useRef<DocsEditorInstance | null>(null);
  const presentationRef = useRef(presentation);
  const lastConfigKeyRef = useRef<string | null>(null);

  // 'A' means slot A is the one currently shown; 'B' means slot B is shown.
  // The idle slot receives the next editor instance.
  const [activeSlot, setActiveSlot] = useState<'A' | 'B'>('A');
  const [hasReadyEditor, setHasReadyEditor] = useState(false);

  // Keep the latest presentation snapshot for image prewarming.
  useEffect(() => {
    presentationRef.current = presentation;
  }, [presentation]);

  const { data, isLoading, isError, error } = useOnlyOfficeConfig(presentation.id, mode);

  const initEditor = useCallback(async () => {
    if (!data) return;

    // Skip if the config object is the same as what we already loaded —
    // the cache revalidated but the underlying doc_key is unchanged.
    const configKey = (data.config as { document?: { key?: string } })?.document?.key;
    if (configKey && configKey === lastConfigKeyRef.current) return;

    const scriptUrl = `${data.onlyoffice_url}/web-apps/apps/api/documents/api.js`;
    const imageUrls = collectDeckImageUrls(presentationRef.current);

    try {
      await Promise.all([loadScript(scriptUrl), prewarmImages(imageUrls)]);
    } catch {
      toast.error('Failed to load the document editor');
      return;
    }

    if (!window.DocsAPI) {
      toast.error('ONLYOFFICE API not available');
      return;
    }

    // Decide which slot the new editor goes into. First init: slot A.
    // Subsequent inits: whichever slot is NOT currently active.
    const firstInit = lastConfigKeyRef.current === null;
    const targetSlot: 'A' | 'B' = firstInit ? 'A' : activeSlot === 'A' ? 'B' : 'A';
    const targetId = targetSlot === 'A' ? SLOT_A_ID : SLOT_B_ID;
    const targetContainer = targetSlot === 'A' ? containerARef.current : containerBRef.current;
    const targetEditorRef = targetSlot === 'A' ? editorARef : editorBRef;
    const oldEditorRef = targetSlot === 'A' ? editorBRef : editorARef;

    // Fresh inner div — DocEditor replaces it with its iframe.
    if (targetContainer) {
      targetContainer.innerHTML = '';
      const inner = document.createElement('div');
      inner.id = targetId;
      targetContainer.appendChild(inner);
    }

    const configWithEvents = {
      ...data.config,
      events: {
        ...((data.config as { events?: Record<string, unknown> }).events || {}),
        onDocumentReady: () => {
          // Promote the new slot to active and tear down the old one.
          setActiveSlot(targetSlot);
          setHasReadyEditor(true);
          if (oldEditorRef.current) {
            try {
              oldEditorRef.current.destroyEditor();
            } catch {
              // destroy errors are non-fatal
            }
            oldEditorRef.current = null;
          }
        },
        onError: () => {
          // Surface a non-blocking flag so the skeleton goes away.
          setHasReadyEditor(true);
        },
      },
    };

    targetEditorRef.current = new window.DocsAPI.DocEditor(targetId, configWithEvents);
    lastConfigKeyRef.current = configKey ?? null;
  }, [data, activeSlot]);

  useEffect(() => {
    initEditor();

    return () => {
      for (const ref of [editorARef, editorBRef]) {
        if (ref.current) {
          try {
            ref.current.destroyEditor();
          } catch {
            // Ignore destroy errors on unmount
          }
          ref.current = null;
        }
      }
      lastConfigKeyRef.current = null;
      setHasReadyEditor(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initEditor]);

  // Show the skeleton overlay only on the very first load — after that,
  // the previous editor stays visible during a swap so there's no flash.
  const showOverlay = !hasReadyEditor && !isError && isLoading;

  return (
    <div className={styles.container}>
      <div className={styles.editorWrapper}>
        {showOverlay && (
          <div className={styles.loadingOverlay}>
            <div className={styles.skeletonCanvas}>
              <div className={styles.skeletonSlide} />
              <div className={styles.skeletonThumbs}>
                <div className={styles.skeletonThumb} />
                <div className={styles.skeletonThumb} />
                <div className={styles.skeletonThumb} />
                <div className={styles.skeletonThumb} />
              </div>
            </div>
            <div className={styles.loadingCaption}>
              <div className={styles.spinner} />
              <p>Preparando el editor…</p>
            </div>
          </div>
        )}

        {isError && (
          <div className={styles.errorState}>
            <p>Failed to load the editor</p>
            <p className={styles.errorDetail}>
              {error?.message || 'Could not retrieve the editor configuration'}
            </p>
          </div>
        )}

        <div
          ref={containerARef}
          className={styles.editorContainer}
          style={{
            opacity: activeSlot === 'A' ? 1 : 0,
            pointerEvents: activeSlot === 'A' ? 'auto' : 'none',
            transition: 'opacity 0.25s ease',
            zIndex: activeSlot === 'A' ? 1 : 0,
          }}
        >
          <div id={SLOT_A_ID} />
        </div>

        <div
          ref={containerBRef}
          className={styles.editorContainer}
          style={{
            opacity: activeSlot === 'B' ? 1 : 0,
            pointerEvents: activeSlot === 'B' ? 'auto' : 'none',
            transition: 'opacity 0.25s ease',
            zIndex: activeSlot === 'B' ? 1 : 0,
          }}
        >
          <div id={SLOT_B_ID} />
        </div>
      </div>
    </div>
  );
};
